import { CSSProperties } from "react"
import * as Popover from "@radix-ui/react-popover"
import { useState } from "react"
import "./select.css"
import { CheckBoxBlank, CheckBoxChecked, Clear, CloseOutlined, DownOutlined, RightOutlined } from "./icons"
import { translate } from "../app"
import { translations } from "../translations"

interface OptionType {
  children?: OptionType[]
  value?: any
  title?: string
  hidden?: boolean
}

interface TreeDataItemProps {
  item: OptionType
  indent?: number
  value?: any
  selectedValues: any[]
  parentSelected?: boolean
  onSelected: (value: any) => void
}

function hasChildrenSelected(item: OptionType, values: any[], counter: { total: number, selected: number }) {
  if (!item.children) item.children = []
  counter.total += item.children.length
  for (let child of item.children) {
    if (values.includes(child.value)) {
      counter.selected++
    }
    hasChildrenSelected(child, values, counter)
  }
  return false
}


function filterNodes(item: OptionType, input: string, flt: (input: string, node: OptionType) => boolean) {

  const visible = flt(input, item)
  item.hidden = !visible

  for (let child of item.children || []) {
    filterNodes(child, input, flt)
  }

  if (!item.hidden || !item.children || !item.children.length) return

  const allChilds = [...item.children, ...(item.children || []).flatMap(v => v.children)]
  const some = allChilds.some(v => {
    if (!v) return false
    return !v.hidden
  })

  if (some) {
    item.hidden = false
  }

}


function TreeDataItem(props: TreeDataItemProps) {
  let { item, indent, value, selectedValues, parentSelected } = props
  let [collapsed, setCollapsed] = useState<boolean>(true)
  let { children, hidden } = item

  if (hidden) {
    return null
  }

  indent = indent || 0
  let leftIndent = indent
  const counter = { total: 0, selected: 0 }

  hasChildrenSelected(item, props.selectedValues || [], counter)

  const blank = <CheckBoxBlank className="mt-[0px] mr-1 text-gray-400 size-[18px]" />
  const checked = <CheckBoxChecked className="mt-[0px] mr-1 text-blue-500 size-[18px]" />
  const partialChecked = <div className="size-[10px] border-2 border-gray-300 border-solid ml-[2px] mr-2 mt-[1px] flex justify-center items-center">
    <div className="size-[8px] bg-blue-500" ></div>
  </div>

  let checkedElement = blank

  if (counter.total > 0 && counter.selected > 0) {
    if (counter.total === counter.selected) {
      checkedElement = checked
    } else if (counter.selected > 0) {
      checkedElement = partialChecked
    }
  } else {
    if (selectedValues.includes(item.value) || parentSelected) {
      checkedElement = checked
      parentSelected = true
    }
  }

  function toggleCollapsed() {
    setCollapsed(!collapsed)
  }

  return <>
    <div
      className="text-black cursor-pointer py-[6px] pl-2 text-sm overflow-x-hidden hover:bg-gray-100"
      style={{ paddingLeft: leftIndent, width: `calc(100% - ${leftIndent}px)` }}
    >
      <div className="flex ">
        <div className="w-5 items-center justify-center flex">
          {!collapsed && children && children.length > 0 && <DownOutlined className="text-gray-400 px-1" onClick={toggleCollapsed} />}
          {collapsed && children && children.length > 0 && <RightOutlined className="text-gray-400 pr-1" onClick={toggleCollapsed} />}
        </div>
        <div className="flex" onMouseDown={(e) => {
          e.preventDefault()
          props.onSelected(item.value)
        }}>
          <div>
            {checkedElement}
          </div>
          <div className="mt-[1px]">
            {item.title}
          </div>
        </div>
      </div>
    </div>
    {children && children.length > 0 && !collapsed && children.map((child) => <TreeDataItem
      onSelected={props.onSelected}
      key={child.value}
      item={child}
      indent={leftIndent + 16}
      selectedValues={selectedValues}
      parentSelected={parentSelected}
    />)}
  </>

}

export function TreeSelect(props: Props) {

  let { treeData, placeholder, onChange, filterTreeNode, value } = props
  const [textValue, setTextValue] = useState<string>("")
  const [option, setOption] = useState<{ label: string; value: string } | null>(null)

  function onChangeInternal
    (value: string) {
    setOption(null)
    setTextValue(value)
  }

  function onSelectInternal(v: any) {
    if (!value.includes(v)) {
      value.push(v)
    } else {
      value.splice(value.indexOf(v), 1)
    }
    onChange?.(value)
    setTextValue("")
  }

  function onRemoveItem(v: any) {
    value.splice(value.indexOf(v), 1)
    onChange?.(value)
  }

  const listHeight = 400
  filterTreeNode = filterTreeNode || ((inputValue: string, option?: any) => true)

  const values = props.value || []
  const allItems = [...treeData, ...treeData.flatMap(v => v.children)]
  const selected = allItems.filter(v => values.includes(v?.value ?? props.defaultValue))

  for (let item of treeData) {
    filterNodes(item, textValue, filterTreeNode)
  }

  return <Popover.Root>
    <Popover.Trigger asChild className="">
      <div className="w-[99%] flex border-gray-300 border-solid border rounded-md text-black bg-white focus-within:border-blue-400">
        <div className="flex flex-wrap justify-start  pr-4 flex-grow">

          {selected.map((v, i) => {
            return <div className="text-xs bg-white border border-gray-200 rounded px-2 py-1 my-1 mx-1 flex items-center" key={i}>
              <div>{v.title}</div>
              <button type="button" className="ml-1 bg-transparent border-0 p-0 cursor-pointer" aria-label={`${translate(translations.close)} ${v.title}`} onClick={() => { onRemoveItem(v.value) }}>
                <CloseOutlined className="text-gray-400 hover:text-gray-600" aria-hidden="true" />
              </button>
            </div>
          })}
          <div className="flex-1">
            <input
              type="text"
              className="w-full outline-none border-0 border-solid border-black placeholder:text-gray-300 pt-1 pl-1 mt-1 bg-transparent"
              placeholder={placeholder}
              aria-label={placeholder}
              onChange={(e) => onChangeInternal(e.target.value)}
              value={textValue} />
          </div>
        </div>
        <div className="pt-2 pr-1 text-sm text-gray-300">
          <DownOutlined aria-hidden="true" />
        </div>
      </div>
    </Popover.Trigger>
    <Popover.Portal>
      <Popover.Content
        id="popover-content"
        className="text-black rounded overflow-y-auto overflow-x-hidden bg-white p-2 shadow-[0_10px_38px_-10px_hsla(206,22%,7%,.35),0_10px_20px_-15px_hsla(206,22%,7%,.2)] PopoverContent"
        style={{ maxHeight: listHeight }}
        sideOffset={5}
        onOpenAutoFocus={(e) => e.preventDefault()}
        asChild
      >
        <div>
          {treeData.map((item, index) => <TreeDataItem
            onSelected={onSelectInternal}
            key={item.value}
            item={item}
            indent={0}
            selectedValues={values} />)}
        </div>
      </Popover.Content>
    </Popover.Portal>
  </Popover.Root>

}

interface Props {
  placeholder?: string
  className?: string
  onChange?: (value: any, labelList?: React.ReactNode[]) => void
  treeData?: OptionType[]
  defaultValue?: any
  value?: any[]
  onDropdownVisibleChange?: (open: boolean) => void
  filterTreeNode?: ((inputValue: string, treeNode?: OptionType) => boolean)
}
